import React, { useState, useRef, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import icsAssistant from '../../assets/appicon.png';
import { ASSISTANT_WEBSOCKET, PROFILE_COMPANY_PIC, COMPANY_API, getApiUrl } from '../../helpers/url_helper';
import '../../styles/styles.css';
import './Assistant.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { AlertDialog } from '../Common';
import AssistantRightSidebar from './AssistantRightSidebar';
import AssistantProjectStartView from './AssistantProjectStartView';

/**
 * Models often put `<br>` inside GFM table cells. react-markdown does not parse raw HTML there
 * (no rehype-raw), so those tags show as plain text. We only interpret this harmless pattern in cells.
 */
const LITERAL_BR_IN_CELL = /<br\s*\/?>/gi;

function renderMarkdownCellChildren(children) {
  if (children == null || children === false) return children;
  return React.Children.map(children, mapMarkdownCellChild);
}

function mapMarkdownCellChild(child, index) {
  if (typeof child === 'string') {
    const parts = child.split(LITERAL_BR_IN_CELL);
    if (parts.length === 1) return child;
    return (
      <React.Fragment key={`cell-frag-${index}`}>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <br /> : null}
            {part}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }
  if (React.isValidElement(child) && child.props.children != null) {
    return React.cloneElement(
      child,
      { key: child.key ?? `cell-el-${index}` },
      renderMarkdownCellChildren(child.props.children)
    );
  }
  return child;
}

function Assistant() {
  const { mode } = useThemeMode();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [responseEnded, setResponseEnded] = useState(false);
  // Add a ref to always have the latest currentResponse
  const currentResponseRef = useRef('');
  // Ref mirrors responseEnded for WebSocket onmessage — the handler closes over stale state from the last connectWebSocket() call, which could skip [END] and leave isLoading stuck true after reconnect.
  const responseEndedRef = useRef(false);
  // Keep refs in sync with state
  useEffect(() => {
    currentResponseRef.current = currentResponse;
  }, [currentResponse]);

  useEffect(() => {
    responseEndedRef.current = responseEnded;
  }, [responseEnded]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [showThinkingText, setShowThinkingText] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');

  // Right sidebar (ChatGPT-style): chat history + projects list
  const RIGHT_SIDEBAR_WIDTH = 284; // px (expanded)
  const RIGHT_SIDEBAR_COLLAPSED_WIDTH = 64; // px (rail)
  const HISTORY_STORAGE_KEY = 'assistant_chat_history_v1';
  const MAX_HISTORY_ITEMS = 50;
  const CHAT_SESSION_MESSAGES_LIMIT = 10;

  const navigate = useNavigate();
  const location = useLocation();

  const [chatHistory, setChatHistory] = useState([]);
  const [activeChatHistoryId, setActiveChatHistoryId] = useState(null);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  const [selectedAssistantProject, setSelectedAssistantProject] = useState(null);
  const [showProjectStartView, setShowProjectStartView] = useState(false);

  const staticProjects = [
    { id: 'p1', title: 'CPC' },
    { id: 'p2', title: 'Qualfon' },
    { id: 'p3', title: 'docmanagement' },
    { id: 'p4', title: 'Techwise Website' },
  ];

  const websocket = useRef(null);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const streamingBubbleRef = useRef(null);
  const hasScrolledForResponseRef = useRef(false);
  const lastMessageRef = useRef(null);
  const lastMessageIndexRef = useRef(-1);
  
  // WebSocket connection management refs
  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastHeartbeatTimeRef = useRef(null);
  const isReconnectingRef = useRef(false);
  const isTabVisibleRef = useRef(true);
  const connectionTokenRef = useRef(null);
  const pendingMessageRef = useRef(null);
  const reconnectCheckAttemptsRef = useRef(0);
  const suppressAutoScrollRef = useRef(false);
  const sessionMessagesOffsetRef = useRef(0);
  const sessionMessagesHasMoreRef = useRef(false);
  const sessionMessagesLoadingRef = useRef(false);
  const activeLoadedSessionIdRef = useRef(null);
  const forceScrollAfterHistoryLoadRef = useRef(false);

  const scheduleScrollToBottom = (behavior = 'smooth') => {
    [0, 80, 180, 320, 500].forEach((delay) => {
      setTimeout(() => scrollToBottom(behavior, true), delay);
    });
  };

  const forceScrollToBottomWithRetries = (attempt = 0) => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      try {
        container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
      } catch (e) {
        // ignore
      }
    }

    const shouldRetry = (!container || container.scrollTop + container.clientHeight < container.scrollHeight - 2) && attempt < 14;
    if (shouldRetry) {
      setTimeout(() => forceScrollToBottomWithRetries(attempt + 1), 80);
    }
  };
  
  // State for attach file menu
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const handleAttachClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddImage = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.accept = 'image/*';
      fileInputRef.current.click();
    }
  };
  const handleAddFile = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.accept = 'application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
      fileInputRef.current.click();
    }
  };
  // State for image preview and uploaded URL
  const [, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  // State for document files
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState(null);
  const [documentContext, setDocumentContext] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: 'error',
    title: '',
    message: ''
  });
  // State for drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Helper to get full image URL from API response
  const getFullImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    // Always use PROFILE_COMPANY_PIC as the base
    return PROFILE_COMPANY_PIC + path.replace(/^\//, '');
  };

  // Upload image to API and set preview to uploaded URL
  const uploadChatImage = async (file) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(getApiUrl(COMPANY_API.CHAT_IMAGE_UPLOAD), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.url) {
        const url = getFullImageUrl(data.data.url);
        setUploadedImageUrl(url);
        setImagePreviewUrl(url);
        // Clear document context for image uploads
        setDocumentContext(null);
      } else {
        setUploadedImageUrl(null);
        setImagePreviewUrl(null);
        setDocumentContext(null);
        setAlertConfig({
          type: 'error',
          title: 'Image Upload Failed',
          message: 'Failed to upload image. Please try again.'
        });
        setShowAlert(true);
      }
    } catch (err) {
      setUploadedImageUrl(null);
      setImagePreviewUrl(null);
      setDocumentContext(null);
      setAlertConfig({
        type: 'error',
        title: 'Image Upload Failed',
        message: 'Failed to upload image. Please try again.'
      });
      setShowAlert(true);
    }
  };

  // Upload document to API
  const uploadChatDocument = async (file) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(getApiUrl(COMPANY_API.CHAT_IMAGE_UPLOAD), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.url) {
        const url = getFullImageUrl(data.data.url);
        setUploadedDocumentUrl(url);
        setSelectedDocument(file);
        // Set document context if available
        setDocumentContext(data.data?.doc_context || null);
        // Clear image states for document uploads
        setSelectedImage(null);
        setImagePreviewUrl(null);
        setUploadedImageUrl(null);
      } else {
        setUploadedDocumentUrl(null);
        setSelectedDocument(null);
        setDocumentContext(null);
        setAlertConfig({
          type: 'error',
          title: 'Document Upload Failed',
          message: 'Failed to upload document. Please try again.'
        });
        setShowAlert(true);
      }
    } catch (err) {
      setUploadedDocumentUrl(null);
      setSelectedDocument(null);
      setDocumentContext(null);
      setAlertConfig({
        type: 'error',
        title: 'Document Upload Failed',
        message: 'Failed to upload document. Please try again.'
      });
      setShowAlert(true);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
        uploadChatImage(file);
      } else if (file.type === 'application/pdf' || 
                 file.type === 'application/msword' || 
                 file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 file.type === 'application/vnd.ms-excel' ||
                 file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                 file.type === 'application/vnd.ms-powerpoint' ||
                 file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                 file.type === 'text/plain') {
        setSelectedDocument(file);
        uploadChatDocument(file);
      } else {
        setAlertConfig({
          type: 'error',
          title: 'Invalid File Type',
          message: 'Please select an image, PDF, Word, Excel, PowerPoint, or text file.'
        });
        setShowAlert(true);
      }
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    const documentFile = files.find(file => 
      file.type === 'application/pdf' || 
      file.type === 'application/msword' || 
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-powerpoint' ||
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      file.type === 'text/plain'
    );
    
    if (imageFile) {
      setSelectedImage(imageFile);
      uploadChatImage(imageFile);
    } else if (documentFile) {
      setSelectedDocument(documentFile);
      uploadChatDocument(documentFile);
    } else if (files.length > 0) {
      setAlertConfig({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please drop an image, PDF, Word, Excel, PowerPoint, or text file.'
      });
      setShowAlert(true);
    }
  };

  const getAuthToken = () => {
    return localStorage.getItem("authToken");
  };

  const sanitizeRemoteImageUrl = (rawUrl) => {
    if (!rawUrl) return '';
    let url = String(rawUrl).trim();
    // Some payloads include wrapped/escaped quotes (e.g. ...png%22)
    url = url.replace(/^"+|"+$/g, '').replace(/%22$/i, '');
    return url;
  };

  const mapApiMessageToUi = (item) => {
    const role = item?.role === 'assistant' ? 'assistant' : 'user';
    const content = item?.content;

    let text = '';
    let image = '';

    if (Array.isArray(content)) {
      const textParts = [];
      content.forEach((part) => {
        if (!part || typeof part !== 'object') return;
        if (part.type === 'text' && part.text != null) {
          const nextText = String(part.text);
          if (nextText.trim()) textParts.push(nextText);
          return;
        }
        if (part.type === 'image_url') {
          const parsedImageUrl = sanitizeRemoteImageUrl(part?.image_url?.url);
          if (parsedImageUrl && !image) {
            image = parsedImageUrl;
          }
        }
      });
      text = textParts.join('\n').trim();
    } else if (content && typeof content === 'object') {
      if (typeof content.text === 'string') {
        text = content.text;
      }
      if (content.image_url?.url) {
        image = sanitizeRemoteImageUrl(content.image_url.url);
      } else if (typeof content.image_url === 'string') {
        image = sanitizeRemoteImageUrl(content.image_url);
      }
    } else {
      text = String(content || '');
    }

    return {
      id: item?.id,
      role,
      text,
      image,
      createdAt: item?.created_at
    };
  };

  const loadSessionMessagesPage = async (sessionId, offset = 0, shouldPrepend = false) => {
    if (!sessionId) return false;
    if (sessionMessagesLoadingRef.current) return false;

    const token = getAuthToken();
    if (!token) return false;

    sessionMessagesLoadingRef.current = true;
    try {
      const endpoint = `company/chat-sessions/${encodeURIComponent(sessionId)}/messages`;
      const response = await fetch(getApiUrl(endpoint, { limit: CHAT_SESSION_MESSAGES_LIMIT, offset }), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.status !== 'success') {
        throw new Error(data?.message || `Failed to load chat messages (${response.status})`);
      }

      const rows = Array.isArray(data?.data) ? data.data : [];
      const normalized = rows.map(mapApiMessageToUi);
      const pageAscending = normalized;

      if (shouldPrepend) {
        // API is ASC: next offsets are newer messages, append to bottom.
        setMessages((prev) => [...prev, ...pageAscending]);
      } else {
        setMessages(pageAscending);
        forceScrollAfterHistoryLoadRef.current = true;
        setTimeout(() => forceScrollToBottomWithRetries(0), 0);
      }

      sessionMessagesOffsetRef.current = offset + rows.length;
      sessionMessagesHasMoreRef.current = rows.length === CHAT_SESSION_MESSAGES_LIMIT;
      activeLoadedSessionIdRef.current = String(sessionId);
      return true;
    } catch (error) {
      console.error('Failed to load chat session messages:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'error', text: `Failed to load chat messages: ${error.message}` }
      ]);
      return false;
    } finally {
      sessionMessagesLoadingRef.current = false;
    }
  };

  const openExistingChatSession = async (historyItem) => {
    const rawSessionId = historyItem?.sessionId ?? historyItem?.chat_session_id ?? historyItem?.id;
    if (!rawSessionId) return;

    // Persist current chat before switching
    saveCurrentConversationToHistory();

    const nextSessionId = String(rawSessionId);

    try {
      if (websocket.current) {
        websocket.current.close(1000, 'Switching chat history session');
      }
    } catch (e) {
      // ignore
    }
    websocket.current = null;

    stopHeartbeat();
    pendingMessageRef.current = null;

    setMessages([]);
    setMessage('');
    setCurrentResponse('');
    responseEndedRef.current = true;
    setResponseEnded(true);
    setIsLoading(false);
    setHasNewMessages(false);
    setShowScrollButton(false);

    setSelectedImage(null);
    setImagePreviewUrl(null);
    setUploadedImageUrl(null);
    setSelectedDocument(null);
    setUploadedDocumentUrl(null);
    setDocumentContext(null);

    setActiveChatHistoryId(nextSessionId);
    localStorage.setItem('chat_session_id', nextSessionId);

    sessionMessagesOffsetRef.current = 0;
    sessionMessagesHasMoreRef.current = false;
    sessionMessagesLoadingRef.current = false;
    activeLoadedSessionIdRef.current = nextSessionId;

    await loadSessionMessagesPage(nextSessionId, 0, false);

    setConnectionStatus('Disconnected');
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const token = connectionTokenRef.current || getAuthToken();
    if (token && isAuthenticated()) {
      setTimeout(() => {
        connectWebSocket(token, false);
      }, 100);
    }
  };



  // Function to format table data for better document compatibility
  const formatTableForDocuments = (tableRows) => {
    if (tableRows.length === 0) return [];
    
    const formattedLines = [];
    const maxColumns = Math.max(...tableRows.map(row => row.length));
    
    // Calculate column widths for better formatting
    const columnWidths = [];
    for (let col = 0; col < maxColumns; col++) {
      let maxWidth = 0;
      for (let row = 0; row < tableRows.length; row++) {
        if (tableRows[row][col]) {
          maxWidth = Math.max(maxWidth, tableRows[row][col].length);
        }
      }
      columnWidths[col] = maxWidth;
    }
    
    // Format each row with proper spacing
    for (let rowIndex = 0; rowIndex < tableRows.length; rowIndex++) {
      const row = tableRows[rowIndex];
      const formattedCells = [];
      
      for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
        const cellContent = row[colIndex] || '';
        const padding = ' '.repeat(Math.max(0, columnWidths[colIndex] - cellContent.length));
        formattedCells.push(cellContent + padding);
      }
      
      // Join cells with tabs for better document compatibility
      formattedLines.push(formattedCells.join('\t'));
    }
    
    // Add a blank line after the table for better separation
    formattedLines.push('');
    
    return formattedLines;
  };

  // Function to generate HTML content for better document compatibility
  const generateHTMLContent = (text, isMarkdown) => {
    if (!isMarkdown) {
      return `<p>${text.replace(/\n/g, '<br>')}</p>`;
    }

    let htmlContent = text
      // Convert markdown to HTML
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
      .replace(/`(.*?)`/g, '<code>$1</code>') // Inline code
      .replace(/```[\s\S]*?```/g, (match) => {
        // Code blocks
        const code = match.replace(/```[\w]*\n?/g, '').replace(/```/g, '');
        return `<pre><code>${code}</code></pre>`;
      })
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>') // Links
      .replace(/^#{1,6}\s+(.+)$/gm, (match, content) => {
        // Headings
        const level = match.match(/^#{1,6}/)[0].length;
        return `<h${level}>${content}</h${level}>`;
      });

    // Handle lists properly
    const lines = htmlContent.split('\n');
    const processedLines = [];
    let inTable = false;
    let tableRows = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let listItems = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for table content
      if (line.includes('|') && line.trim().length > 0) {
        // Process any accumulated list items first
        if (inUnorderedList && listItems.length > 0) {
          processedLines.push(`<ul>${listItems.join('')}</ul>`);
          listItems = [];
          inUnorderedList = false;
        }
        if (inOrderedList && listItems.length > 0) {
          processedLines.push(`<ol>${listItems.join('')}</ol>`);
          listItems = [];
          inOrderedList = false;
        }
        
        // Skip separator rows (rows with only dashes, colons, and spaces)
        const isSeparatorRow = line.replace(/[|\s:]/g, '').replace(/-/g, '').length === 0;
        
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        
        // Clean the table row and add to collection (skip separator rows)
        if (!isSeparatorRow) {
          const cleanRow = line
            .replace(/^\||\|$/g, '')
            .split('|')
            .map(cell => cell.trim())
            .filter(cell => cell.length > 0);
          
          if (cleanRow.length > 0) {
            tableRows.push(cleanRow);
          }
        }
      } else {
        // Process any accumulated table rows
        if (inTable && tableRows.length > 0) {
          processedLines.push(generateHTMLTable(tableRows));
          tableRows = [];
          inTable = false;
        }
        
        // Check for unordered list items
        const unorderedMatch = line.match(/^\s*[-*+]\s+(.+)$/);
        if (unorderedMatch) {
          if (!inUnorderedList) {
            // End ordered list if we were in one
            if (inOrderedList && listItems.length > 0) {
              processedLines.push(`<ol style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ol>`);
              listItems = [];
              inOrderedList = false;
            }
            inUnorderedList = true;
          }
          listItems.push(`<li style="margin: 5px 0;">${unorderedMatch[1]}</li>`);
          continue;
        }
        
        // Check for ordered list items
        const orderedMatch = line.match(/^\s*(\d+)\.\s+(.+)$/);
        if (orderedMatch) {
          if (!inOrderedList) {
            // End unordered list if we were in one
            if (inUnorderedList && listItems.length > 0) {
              processedLines.push(`<ul style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ul>`);
              listItems = [];
              inUnorderedList = false;
            }
            inOrderedList = true;
          }
          // Preserve the original number by adding the value attribute
          listItems.push(`<li style="margin: 5px 0;" value="${orderedMatch[1]}">${orderedMatch[2]}</li>`);
          continue;
        }
        
        // If we reach here, it's not a list item, so end any current list
        if (inUnorderedList && listItems.length > 0) {
          processedLines.push(`<ul style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ul>`);
          listItems = [];
          inUnorderedList = false;
        }
        if (inOrderedList && listItems.length > 0) {
          processedLines.push(`<ol style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ol>`);
          listItems = [];
          inOrderedList = false;
        }
        
        // Add non-table, non-list line
        if (line.trim().length > 0) {
          processedLines.push(line);
        }
      }
    }
    
    // Handle any remaining table at the end
    if (inTable && tableRows.length > 0) {
      processedLines.push(generateHTMLTable(tableRows));
    }
    
    // Handle any remaining list items at the end
    if (inUnorderedList && listItems.length > 0) {
      processedLines.push(`<ul style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ul>`);
    }
    if (inOrderedList && listItems.length > 0) {
      processedLines.push(`<ol style="margin: 10px 0; padding-left: 20px;">${listItems.join('')}</ol>`);
    }
    
    htmlContent = processedLines.join('\n');
    
    // Wrap in paragraphs
    htmlContent = htmlContent
      .split('\n\n')
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => {
        if (paragraph.startsWith('<h') || paragraph.startsWith('<pre') || 
            paragraph.startsWith('<table') || paragraph.startsWith('<ul') || 
            paragraph.startsWith('<ol') || paragraph.startsWith('<li>')) {
          return paragraph;
        }
        return `<p>${paragraph}</p>`;
      })
      .join('\n');
    
    return htmlContent;
  };

  // Function to generate HTML table
  const generateHTMLTable = (tableRows) => {
    if (tableRows.length === 0) return '';

    const escapeHtml = (value) => {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    const renderCellHtml = (value) => {
      const safe = escapeHtml(value);
      // Preserve line breaks when model used literal "<br>" inside markdown table cells.
      // We only interpret this one safe pattern for clipboard HTML.
      return safe.replace(LITERAL_BR_IN_CELL, '<br />');
    };

    const tableStyle = [
      'border-collapse:collapse',
      'width:100%',
      'table-layout:fixed',
      'font-family:Arial, sans-serif',
      'font-size:14px',
      'color:#111',
      'background:#fff'
    ].join(';');

    const headerCellStyle = [
      'border:1px solid #111',
      'padding:8px 10px',
      'text-align:left',
      'font-weight:600',
      'vertical-align:top',
      'background:#f5f5f5',
      'white-space:pre-wrap',
      'word-break:break-word'
    ].join(';');

    const cellStyle = [
      'border:1px solid #111',
      'padding:8px 10px',
      'vertical-align:top',
      'background:#fff',
      'white-space:pre-wrap',
      'word-break:break-word'
    ].join(';');

    let tableHTML = `<table style="${tableStyle}">`;

    for (let i = 0; i < tableRows.length; i++) {
      const row = tableRows[i];
      const isHeaderRow = i === 0;
      const tag = isHeaderRow ? 'th' : 'td';
      const style = isHeaderRow ? headerCellStyle : cellStyle;

      tableHTML += '<tr>';
      for (let j = 0; j < row.length; j++) {
        tableHTML += `<${tag} style="${style}">${renderCellHtml(row[j])}</${tag}>`;
      }
      tableHTML += '</tr>';
    }

    tableHTML += '</table>';
    return tableHTML;
  };

  // Copy rendered content to clipboard function
  const copyToClipboard = async (text, isMarkdown = true) => {
    let contentToCopy = text;
    
    if (isMarkdown) {
      // Enhanced markdown to plain text conversion with better table handling
      contentToCopy = text
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers but keep text
        .replace(/\*(.*?)\*/g, '$1') // Remove italic markers but keep text
        .replace(/`(.*?)`/g, '$1') // Remove code markers but keep text
        .replace(/```[\s\S]*?```/g, (match) => {
          // For code blocks, extract the content without the markers
          return match.replace(/```[\w]*\n?/g, '').replace(/```/g, '');
        })
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
        .replace(/^#{1,6}\s+/gm, '') // Remove heading markers
        .replace(/^\s*[-*+]\s+/gm, '- ') // Convert list markers to bullets
        .replace(/^\s*(\d+)\.\s+/gm, (match, num) => {
          // Convert numbered lists to plain numbers, preserving the original number
          return `${num}. `;
        })
        .replace(/\n\s*\n/g, '\n\n'); // Clean up extra newlines
      
      // Enhanced table formatting for better document compatibility
      const lines = contentToCopy.split('\n');
      const processedLines = [];
      let inTable = false;
      let tableRows = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Check if this line contains table content (has | characters)
        if (line.includes('|') && line.trim().length > 0) {
          // Skip separator rows (rows with only dashes, colons, and spaces)
          const isSeparatorRow = line.replace(/[|\s:]/g, '').replace(/-/g, '').length === 0;
          
          if (!inTable) {
            inTable = true;
            tableRows = [];
          }
          
          // Clean the table row and add to collection (skip separator rows)
          if (!isSeparatorRow) {
            const cleanRow = line
              .replace(/^\||\|$/g, '') // Remove leading/trailing pipes
              .split('|')
              .map(cell => cell.trim())
              .filter(cell => cell.length > 0); // Remove empty cells
            
            if (cleanRow.length > 0) {
              tableRows.push(cleanRow);
            }
          }
        } else {
          // Process any accumulated table rows
          if (inTable && tableRows.length > 0) {
            processedLines.push(...formatTableForDocuments(tableRows));
            tableRows = [];
            inTable = false;
          }
          
          // Add non-table line
          if (line.trim().length > 0) {
            processedLines.push(line);
          }
        }
      }
      
      // Handle any remaining table at the end
      if (inTable && tableRows.length > 0) {
        processedLines.push(...formatTableForDocuments(tableRows));
      }
      
      contentToCopy = processedLines.join('\n').trim();
    }
    
    try {
      // Try to copy as both text and HTML for better document compatibility
      const clipboardData = [
        new ClipboardItem({
          'text/plain': new Blob([contentToCopy], { type: 'text/plain' }),
          'text/html': new Blob([generateHTMLContent(text, isMarkdown)], { type: 'text/html' })
        })
      ];
      
      await navigator.clipboard.write(clipboardData);
      
      // Show success feedback
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Hide after 2 seconds
      
    } catch (err) {
      console.error('Failed to copy with HTML format, falling back to text only: ', err);
      
      try {
        // Fallback to text-only copy
        await navigator.clipboard.writeText(contentToCopy);
        
        // Show success feedback
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        
      } catch (textErr) {
        console.error('Failed to copy text: ', textErr);
        // Final fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = contentToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        // Show success feedback even for fallback
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }
  };

  // Check if user is authenticated using session manager
  const isAuthenticated = () => {
    const token = getAuthToken();
    if (!token) return false;
    
    // Check if session is expired
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) return false;
    
    const now = Date.now();
    const timeSinceLastActivity = now - parseInt(lastActivity);
    const sessionTimeout = 8 * 60 * 60 * 1000; // 8 hours
    
    return timeSinceLastActivity <= sessionTimeout;
  };

  // Expose scroll function globally for debugging
  useEffect(() => {
    window.scrollToBottom = scrollToBottom;
    return () => {
      delete window.scrollToBottom;
    };
  }, []);

  // Get user first name from localStorage
  useEffect(() => {
    const getUserFirstName = () => {
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const user = JSON.parse(userData);
          const fullName = user.full_name || '';
          // Extract first name from full name
          if (fullName) {
            const firstName = fullName.split(' ')[0];
            setUserFirstName(firstName);
          }
        }
      } catch (error) {
        console.error('Error getting user first name:', error);
      }
    };
    getUserFirstName();
  }, []);

  // Load chat history from localStorage (client-side)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setChatHistory(parsed);

        // If we have a session stored, prefer highlighting the matching chat.
        const currentSessionId = localStorage.getItem('chat_session_id');
        if (currentSessionId) {
          const match = parsed.find((c) => String(c.sessionId) === String(currentSessionId));
          if (match) setActiveChatHistoryId(match.id);
        }
      }
    } catch (e) {
      console.error('Failed to load assistant chat history:', e);
    }
  }, []);

  const activeProjectIdFromPath = (() => {
    const match = location?.pathname?.match(/^\/project\/([^/]+)\/tasks/);
    return match ? match[1] : null;
  })();
  const activeProjectId = selectedAssistantProject?.id ?? activeProjectIdFromPath;

  useEffect(() => {
    const token = getAuthToken();
    if (token && isAuthenticated()) {
      connectWebSocket(token);
    } else {
      setMessages([{ role: 'error', text: 'Authentication required. Please login first.' }]);
      // Redirect to login if not authenticated
      window.location.href = '/login';
    }
    
    return () => {
      // Cleanup: stop heartbeat and close connection
      stopHeartbeat();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (websocket.current) {
        try {
          websocket.current.close();
        } catch (e) {
          // Ignore errors during cleanup
        }
        websocket.current = null;
      }
      // Reset reconnection state
      isReconnectingRef.current = false;
      reconnectAttemptsRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, connectWebSocket and isAuthenticated are stable

  // Page Visibility API: Listen for tab visibility changes
  useEffect(() => {
    // Set initial visibility state
    isTabVisibleRef.current = !document.hidden;
    
    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // handleVisibilityChange uses refs and is stable

  // Periodic connection health check (every 30 seconds)
  useEffect(() => {
    const healthCheckInterval = setInterval(() => {
      checkConnectionHealth();
    }, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(healthCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // checkConnectionHealth uses refs and is stable

  // Show extended thinking text if assistant takes longer to start responding
  useEffect(() => {
    let timeoutId;
    if (isLoading && !currentResponse) {
      // After ~4.5s, show the extended thinking message
      timeoutId = setTimeout(() => setShowThinkingText(true), 4500);
    } else {
      setShowThinkingText(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, currentResponse]);

  // ========== WebSocket Connection Management Functions ==========
  
  /**
   * Respond to server's PING message with PONG
   * Backend sends [PING] every 30 seconds, we respond with [PONG]
   */
  const respondToPing = () => {
    if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
      try {
        // Respond to server's ping with pong
        // Backend expects [PONG] or {"type": "pong"}
        websocket.current.send(JSON.stringify({ type: 'pong' }));
        lastHeartbeatTimeRef.current = Date.now();
      } catch (error) {
        console.error('Failed to send pong response:', error);
        // If send fails, connection might be broken, trigger reconnect
        if (!isReconnectingRef.current) {
          reconnectWebSocket();
        }
      }
    }
  };

  /**
   * Start heartbeat monitoring (we don't send pings, we respond to server pings)
   * The backend sends [PING] every 30 seconds, we just need to be ready to respond
   */
  const startHeartbeat = () => {
    // Clear any existing heartbeat interval (we don't need to send anything)
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    // Initialize heartbeat time
    lastHeartbeatTimeRef.current = Date.now();
    
    // No need to send messages - we just respond to server's [PING] messages
    // The server sends [PING] every 30 seconds, and we respond in onmessage handler
  };

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  /**
   * Reconnect WebSocket with exponential backoff
   */
  const reconnectWebSocket = () => {
    // Prevent multiple simultaneous reconnection attempts
    if (isReconnectingRef.current) {
      return;
    }

    const token = connectionTokenRef.current || getAuthToken();
    if (!token || !isAuthenticated()) {
      // If we can't authenticate, clear pending messages
      if (pendingMessageRef.current) {
        pendingMessageRef.current = null;
        setIsLoading(false);
        setMessages(prev => [...prev, { role: 'error', text: 'Authentication required. Please refresh the page and try again.' }]);
      }
      return;
    }

    isReconnectingRef.current = true;
    setConnectionStatus('Reconnecting...');

    // Calculate backoff delay: 1s, 2s, 4s, 8s, max 30s
    const attempt = reconnectAttemptsRef.current;
    const delay = Math.min(1000 * Math.pow(2, attempt), 30000);

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Set a maximum reconnection timeout (30 seconds total)
    // If reconnection takes too long, clear pending message and show error
    const maxReconnectTimeout = setTimeout(() => {
      if (pendingMessageRef.current && websocket.current?.readyState !== WebSocket.OPEN) {
        console.log('Reconnection timeout - clearing pending message');
        pendingMessageRef.current = null;
        setIsLoading(false);
        setMessages(prev => [...prev, { role: 'error', text: 'Connection timeout. Please try sending your message again.' }]);
        setConnectionStatus('Disconnected');
      }
    }, 30000);

    reconnectTimeoutRef.current = setTimeout(() => {
      // Clear the max timeout
      clearTimeout(maxReconnectTimeout);
      
      reconnectAttemptsRef.current++;
      
      // Check if already connected (might have been reconnected manually)
      if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
        isReconnectingRef.current = false;
        setConnectionStatus('Connected');
        reconnectAttemptsRef.current = 0;
        startHeartbeat();
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        return;
      }

      // Close existing connection if any
      if (websocket.current) {
        try {
          websocket.current.close();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }

      // Attempt to reconnect
      connectWebSocket(token);
    }, delay);
  };

  /**
   * Validate and ensure WebSocket connection is ready
   * Returns true if connection is open, false otherwise
   */
  const validateConnection = () => {
    // Check if WebSocket exists and is in OPEN state
    if (!websocket.current) {
      return false;
    }
    
    const readyState = websocket.current.readyState;
    
    // WebSocket states: CONNECTING (0), OPEN (1), CLOSING (2), CLOSED (3)
    if (readyState === WebSocket.OPEN) {
      return true;
    }
    
    // Connection is not open - update status and return false
    if (readyState === WebSocket.CLOSED || readyState === WebSocket.CLOSING) {
      if (connectionStatus !== 'Disconnected' && connectionStatus !== 'Connecting' && connectionStatus !== 'Reconnecting...') {
        setConnectionStatus('Disconnected');
      }
    }
    
    return false;
  };

  /**
   * Handle tab visibility changes
   */
  const handleVisibilityChange = () => {
    const isVisible = !document.hidden;
    const wasVisible = isTabVisibleRef.current;
    isTabVisibleRef.current = isVisible;
    
    // When tab becomes visible after being hidden, check and fix connection
    if (isVisible && !wasVisible) {
      // First, check if WebSocket connection is actually open
      const isConnectionOpen = validateConnection();
      
      if (!isConnectionOpen) {
        // Connection is closed - reconnect immediately
        console.log('Tab became visible: Connection is closed, reconnecting...');
        reconnectWebSocket();
        return;
      }
      
      // Connection appears open, but verify it's not stale
      if (lastHeartbeatTimeRef.current) {
        const timeSinceLastHeartbeat = Date.now() - lastHeartbeatTimeRef.current;
        // If no ping/pong activity for > 90 seconds, connection might be stale
        if (timeSinceLastHeartbeat > 90000) {
          console.log('Tab became visible: Connection may be stale (no activity for 90s), reconnecting...');
          reconnectWebSocket();
        } else {
          // Connection seems healthy, update heartbeat time
          lastHeartbeatTimeRef.current = Date.now();
        }
      } else {
        // No heartbeat timestamp - initialize it
        lastHeartbeatTimeRef.current = Date.now();
      }
    }
  };

  /**
   * Check connection health periodically
   * Server sends [PING] every 30 seconds, so if we haven't received any activity for 90 seconds,
   * the connection might be stale
   */
  const checkConnectionHealth = () => {
    // First validate if connection is actually open
    const isConnectionOpen = validateConnection();
    
    if (!isConnectionOpen) {
      // Connection is closed - try to reconnect
      console.log('Connection health check: Connection is closed, reconnecting...');
      reconnectWebSocket();
      return;
    }

    // Connection is open - check if it's stale based on heartbeat
    if (lastHeartbeatTimeRef.current) {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatTimeRef.current;
      // If no ping/pong activity in last 90 seconds (3x the server's 30s interval), connection might be stale
      if (timeSinceLastHeartbeat > 90000) {
        console.log('Connection health check: no ping/pong activity for 90s, potential stale connection, reconnecting...');
        reconnectWebSocket();
      }
    } else {
      // If we never received any activity but connection is open, initialize timestamp
      // This might happen right after connection
      lastHeartbeatTimeRef.current = Date.now();
    }
  };

  // ========== Assistant Chat History (client-side persistence) ==========
  const sanitizeMessagesForHistory = (msgs) => {
    return (msgs || []).map((m) => {
      const sanitized = { ...m };

      // `document` may be a File object (not serializable). Keep only name/size for rendering.
      if (sanitized.document) {
        if (typeof sanitized.document === 'object') {
          sanitized.document = {
            name: sanitized.document.name || 'Document',
            size: typeof sanitized.document.size === 'number' ? sanitized.document.size : 0
          };
        } else {
          sanitized.document = { name: 'Document', size: 0 };
        }
      }

      // Ensure image is a string URL (render expects `src` string)
      if (sanitized.image && typeof sanitized.image !== 'string') {
        sanitized.image = '';
      }

      return sanitized;
    });
  };

  const getChatTitleFromMessages = (msgs) => {
    const safe = msgs || [];
    const firstUser = safe.find((m) => m?.role === 'user');

    if (firstUser?.text && String(firstUser.text).trim()) {
      return String(firstUser.text).trim().slice(0, 34);
    }
    if (firstUser?.document?.name) {
      return String(firstUser.document.name).slice(0, 34);
    }

    return 'New conversation';
  };

  const saveCurrentConversationToHistory = () => {
    try {
      if (!messages || messages.length === 0) return;

      const sessionId = localStorage.getItem('chat_session_id');
      if (!sessionId) return;

      const id = String(sessionId);
      const title = getChatTitleFromMessages(messages);
      const sanitizedMessages = sanitizeMessagesForHistory(messages);
      const createdAt = Date.now();

      setChatHistory((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const existing = prevArr.find((c) => String(c.id) === id);

        const nextArr = [
          {
            id,
            sessionId: id,
            title,
            createdAt: existing?.createdAt || createdAt,
            messages: sanitizedMessages
          },
          ...prevArr.filter((c) => String(c.id) !== id)
        ].slice(0, MAX_HISTORY_ITEMS);

        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(nextArr));
        return nextArr;
      });

      setActiveChatHistoryId(id);
    } catch (e) {
      console.error('Failed to save assistant chat history:', e);
    }
  };

  const switchConversationToHistory = (historyItem) => {
    openExistingChatSession(historyItem);
  };

  /**
   * Reset conversation - clears session and starts a new conversation
   */
  const resetConversation = () => {
    // Save current chat before starting a new one
    saveCurrentConversationToHistory();

    // Clear stored session_id
    localStorage.removeItem('chat_session_id');
    
    // Clear current messages
    setMessages([]);
    setCurrentResponse('');
    responseEndedRef.current = false;
    setResponseEnded(false);
    setIsLoading(false);
    setActiveChatHistoryId(null);
    
    // Close current WebSocket connection if exists
    if (websocket.current) {
      try {
        // Close with normal closure code
        websocket.current.close(1000, 'Resetting conversation');
      } catch (e) {
        // Ignore errors during cleanup
      }
      websocket.current = null;
    }
    
    // Stop heartbeat
    stopHeartbeat();
    
    // Clear any pending messages
    pendingMessageRef.current = null;
    sessionMessagesOffsetRef.current = 0;
    sessionMessagesHasMoreRef.current = false;
    sessionMessagesLoadingRef.current = false;
    activeLoadedSessionIdRef.current = null;
    
    // Reset connection state
    setConnectionStatus('Disconnected');
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Reconnect with reset_session=true to start a new conversation
    const token = connectionTokenRef.current || getAuthToken();
    if (token && isAuthenticated()) {
      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        connectWebSocket(token, true); // true = resetSession
      }, 100);
    }
  };

  // ========== End WebSocket Connection Management Functions ==========

  const connectWebSocket = (token, resetSession = false) => {
    setConnectionStatus('Connecting...');
    
    // Store token for reconnection purposes
    connectionTokenRef.current = token;
    
    // Reset reconnection state
    isReconnectingRef.current = false;
    
    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    try {
      // Build WebSocket URL with token
      let wsUrl = `${ASSISTANT_WEBSOCKET}?token=${token}`;
      
      // Handle session_id
      if (resetSession) {
        // If resetting session, clear stored session_id and add reset_session parameter
        localStorage.removeItem('chat_session_id');
        wsUrl += `&reset_session=true`;
      } else {
        // Read stored session_id if it exists
        const sessionId = localStorage.getItem('chat_session_id');
        if (sessionId) {
          wsUrl += `&session_id=${sessionId}`;
          // Optional: Show that we're resuming conversation
          // setConnectionStatus('Connecting... (Resuming conversation)');
        }
      }
      
      websocket.current = new WebSocket(wsUrl);
      
      websocket.current.onopen = () => {
        setConnectionStatus('Connected');
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        reconnectCheckAttemptsRef.current = 0;
        
        // Initialize heartbeat time
        lastHeartbeatTimeRef.current = Date.now();
        
        // Start heartbeat to keep connection alive
        startHeartbeat();
        
        // If there's a pending message, send it now that connection is established
        if (pendingMessageRef.current) {
          const pending = pendingMessageRef.current;
          pendingMessageRef.current = null; // Clear pending message
          
          try {
            console.log('Sending pending message after reconnection:', pending.payload);
            websocket.current.send(JSON.stringify(pending.payload));
            // Message was already added to UI, just ensure loading state is set
            setIsLoading(true);
            responseEndedRef.current = false;
            setResponseEnded(false);
            setCurrentResponse('');
          } catch (error) {
            console.error('Failed to send pending message after reconnection:', error);
            setMessages(prev => [...prev, { role: 'error', text: `Failed to send message after reconnection: ${error.message}` }]);
            setIsLoading(false);
          }
        }
        
        // Don't add any connection message - let user initiate conversation
      };
      websocket.current.onmessage = (event) => {
        const message = event.data;
        
        // Handle connection message with session_id
        // This should be checked first before other message types
        if (message.startsWith('{"type":"connected"') || message.includes('"type":"connected"')) {
          try {
            const data = JSON.parse(message);
            if (data.type === "connected" && data.session_id) {
              // Store session_id in localStorage for persistence across page refreshes
              localStorage.setItem('chat_session_id', data.session_id.toString());
              console.log('Session ID stored:', data.session_id);
              // Show indicator that conversation is resumed (optional)
              // setConnectionStatus('Connected - Resuming conversation');
            }
            return; // Don't process connection message as regular message
          } catch (e) {
            // If parsing fails, continue with regular message handling
            console.error('Failed to parse connection message:', e);
          }
        }
        
        // Handle server's PING message - respond with PONG to keep connection alive
        // Backend sends [PING] every 30 seconds
        if (message === '[PING]' || message.trim() === '[PING]') {
          // Respond to server's ping with pong
          respondToPing();
          // Update heartbeat time
          lastHeartbeatTimeRef.current = Date.now();
          return; // Don't process as regular message
        }
        
        // Handle JSON ping/pong messages
        try {
          const parsed = JSON.parse(message);
          if (parsed.type === 'ping') {
            // Server sent ping, respond with pong
            respondToPing();
            lastHeartbeatTimeRef.current = Date.now();
            return; // Don't process as regular message
          }
          if (parsed.type === 'pong') {
            // Server acknowledged our pong (if we ever send one)
            lastHeartbeatTimeRef.current = Date.now();
            return; // Don't process as regular message
          }
          // Handle connection message in JSON format (if not caught above)
          if (parsed.type === 'connected' && parsed.session_id) {
            localStorage.setItem('chat_session_id', parsed.session_id.toString());
            console.log('Session ID stored:', parsed.session_id);
            return; // Don't process as regular message
          }
        } catch (e) {
          // Not JSON, continue with regular message handling
        }
        
        // Update heartbeat time on any message (server is alive and responding)
        lastHeartbeatTimeRef.current = Date.now();
        
        if (message === '[END]') {
          // Use responseEndedRef — not `responseEnded` state — because this handler is created in connectWebSocket() and would otherwise close over a stale value after reconnect, skipping [END] and leaving isLoading true (frozen UI).
          if (!responseEndedRef.current) {
            responseEndedRef.current = true;
            if (currentResponseRef.current.trim()) {
              setMessages(prevMessages => [
                ...prevMessages,
                { role: 'assistant', text: currentResponseRef.current }
              ]);
              setCurrentResponse('');
            }
            setResponseEnded(true);
          }
          // Always clear loading when the server signals end (covers duplicate [END] and ref/state edge cases).
          setIsLoading(false);
        } else if (message.startsWith('[ERROR]')) {
          const errorText = message.replace('[ERROR] ', '');
          
          // Show errors to user (no need to filter heartbeat errors since we're not sending heartbeat questions)
          setMessages(prev => [...prev, { role: 'error', text: `Error: ${errorText}` }]);
          setIsLoading(false);
          setCurrentResponse('');
          responseEndedRef.current = true;
          setResponseEnded(true);
        } else {
          setCurrentResponse(prev => prev + message);
        }
      };
      websocket.current.onerror = () => {
        // Stop heartbeat on error
        stopHeartbeat();
        
        // Only show connection error if not already reconnecting (to avoid spam)
        if (!isReconnectingRef.current && messages.length > 0) {
          // Don't show error message during silent reconnection attempts
          // Connection will be restored automatically
        }
        setConnectionStatus('Error');
        setIsLoading(false);
        setCurrentResponse('');
        
        // Attempt to reconnect on error (silent reconnection)
        if (!isReconnectingRef.current) {
          reconnectWebSocket();
        }
      };
      
      websocket.current.onclose = (event) => {
        // Stop heartbeat on close
        stopHeartbeat();
        
        setConnectionStatus('Disconnected');
        
        // Only show error message if not reconnecting and not a normal closure
        // Code 1000 = Normal closure, 1001 = Going away (page navigation)
        // Code 1006 = Abnormal closure (network error) - should reconnect
        if (!isReconnectingRef.current) {
          // Only show connection error for abnormal closures and if user has messages
          if (event.code !== 1000 && event.code !== 1001 && messages.length > 0) {
            // Silent reconnection - don't spam user with messages
            // Connection will be restored automatically
          }
        }
        
        setIsLoading(false);
        setCurrentResponse('');
        
        // Attempt to reconnect automatically (except for normal closure codes)
        if (event.code !== 1000 && event.code !== 1001) {
          reconnectWebSocket();
        }
      };
    } catch (error) {
      setConnectionStatus('Error');
      // Only show connection error if there are existing messages (user has started chatting)
      if (messages.length > 0) {
        setMessages(prev => [...prev, { role: 'error', text: `Failed to establish WebSocket connection: ${error.message}` }]);
      }
    }
  };

  const handleSendMessage = (msg) => {
    const textToSend = typeof msg === 'string' ? msg : message;

    const hasText = !!textToSend && textToSend.trim().length > 0;
    const hasDocumentContext = !!documentContext && documentContext.trim().length > 0;
    const hasImage = !!uploadedImageUrl;
    const hasDocument = !!selectedDocument;

    // Do not send completely empty messages (no text and no attachments/context)
    if (!hasText && !hasDocumentContext && !hasImage && !hasDocument) {
      return;
    }

    const trimmedQuestion = hasText ? textToSend.trim() : '';

    // Prepare message payload based on new backend contract
    let messagePayload = {
      // Allow empty question when user only sends image/document
      question: trimmedQuestion,
      image_url: null,
      doc_context: null
    };

    // If we have document context (PDF was uploaded), use doc_context instead of image_url
    if (documentContext && documentContext.trim()) {
      messagePayload.doc_context = documentContext;
    } else {
      // If no document context, check for image URL
      let imageUrlToSend = uploadedImageUrl || '';
      if (imageUrlToSend && !imageUrlToSend.startsWith('http')) {
        imageUrlToSend = PROFILE_COMPANY_PIC + imageUrlToSend.replace(/^\//, '');
      }
      if (imageUrlToSend) {
        messagePayload.image_url = imageUrlToSend;
      }
    }

    // Validate connection before sending
    const isConnectionReady = validateConnection();
    
    if (!isConnectionReady) {
      // Connection is not open - store message and reconnect
      console.log('Connection not ready when sending message, storing message and attempting to reconnect...');
      
      // Store the message and related data to send after reconnection
      pendingMessageRef.current = {
        payload: messagePayload,
        text: textToSend.trim(),
        image: imagePreviewUrl,
        document: selectedDocument
      };
      
      // Show user that we're reconnecting
      if (connectionStatus !== 'Reconnecting...' && connectionStatus !== 'Connecting...') {
        setConnectionStatus('Reconnecting...');
      }
      
      // Reset retry counter
      reconnectCheckAttemptsRef.current = 0;
      
      // Attempt to reconnect (pending message will be sent in onopen handler)
      reconnectWebSocket();
      
      // Show user message immediately (optimistic UI)
      setMessages(prev => [...prev, { role: 'user', text: textToSend.trim(), image: imagePreviewUrl, document: selectedDocument }]);
      setMessage('');
      setIsLoading(true);
      responseEndedRef.current = false;
      setResponseEnded(false);
      setCurrentResponse('');
      setSelectedImage(null);
      setImagePreviewUrl(null);
      setUploadedImageUrl(null);
      setSelectedDocument(null);
      setUploadedDocumentUrl(null);
      setDocumentContext(null);
      
      // Scroll to bottom
      scheduleScrollToBottom('smooth');
      
      return;
    }

    // Connection is ready - send message immediately
    if (connectionStatus === 'Connected') {
      console.log('Sending message payload:', messagePayload);
      try {
        websocket.current.send(JSON.stringify(messagePayload));
        setMessages(prev => [...prev, { role: 'user', text: textToSend.trim(), image: imagePreviewUrl, document: selectedDocument }]);
        setMessage('');
        setIsLoading(true);
        responseEndedRef.current = false;
        setResponseEnded(false);
        setCurrentResponse('');
        setSelectedImage(null);
        setImagePreviewUrl(null);
        setUploadedImageUrl(null);
        setSelectedDocument(null);
        setUploadedDocumentUrl(null);
        setDocumentContext(null);
        scheduleScrollToBottom('smooth');
      } catch (error) {
        console.error('Failed to send message:', error);
        setMessages(prev => [...prev, { role: 'error', text: `Failed to send message: ${error.message}` }]);
        setIsLoading(false);
        // If send fails, connection might be broken - try to reconnect
        if (!isReconnectingRef.current) {
          reconnectWebSocket();
        }
      }
    } else {
      // Connection status is not 'Connected' - show error
      setMessages(prev => [...prev, { role: 'error', text: `Connection not ready. Status: ${connectionStatus}. Please wait a moment and try again.` }]);
    }
  };

  const startProjectConversation = (text, attachmentData = null) => {
    const projectIdRaw = selectedAssistantProject?.id ?? null;
    const trimmed = String(text || '').trim();
    const hasAttachment = !!attachmentData?.uploadedImageUrl || !!attachmentData?.selectedDocument || !!(attachmentData?.documentContext && attachmentData.documentContext.trim().length > 0);
    if (!trimmed && !hasAttachment) return;

    // Save current chat before starting a new one
    saveCurrentConversationToHistory();

    // Force a fresh chat session for this project
    localStorage.removeItem('chat_session_id');

    // Reset UI state
    setMessages([]);
    setMessage('');
    setCurrentResponse('');
    responseEndedRef.current = false;
    setResponseEnded(false);
    setIsLoading(false);
    setActiveChatHistoryId(null);

    // Clear attachments so they don't leak into the new session
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setUploadedImageUrl(null);
    setSelectedDocument(null);
    setUploadedDocumentUrl(null);
    setDocumentContext(null);

    // Close current WebSocket connection if exists
    if (websocket.current) {
      try {
        websocket.current.close(1000, 'Starting project conversation');
      } catch (e) {
        // ignore
      }
      websocket.current = null;
    }

    stopHeartbeat();
    pendingMessageRef.current = null;
    sessionMessagesOffsetRef.current = 0;
    sessionMessagesHasMoreRef.current = false;
    sessionMessagesLoadingRef.current = false;
    activeLoadedSessionIdRef.current = null;

    const projectIdNumber = projectIdRaw != null ? Number(projectIdRaw) : NaN;
    const projectIdToSend = Number.isFinite(projectIdNumber) ? projectIdNumber : undefined;

    const messagePayload = {
      question: trimmed,
      ...(projectIdToSend !== undefined ? { project_id: projectIdToSend } : {}),
      image_url: attachmentData?.uploadedImageUrl || null,
      doc_context: attachmentData?.documentContext || null
    };

    // Optimistic UI
    setMessages((prev) => [...prev, {
      role: 'user',
      text: trimmed,
      image: attachmentData?.imagePreviewUrl || null,
      document: attachmentData?.selectedDocument || null
    }]);
    setIsLoading(true);
    responseEndedRef.current = false;
    setResponseEnded(false);
    setCurrentResponse('');

    // Queue message until WebSocket is connected (onopen will flush it)
    pendingMessageRef.current = {
      payload: messagePayload,
      text: trimmed,
      image: attachmentData?.imagePreviewUrl || null,
      document: attachmentData?.selectedDocument || null
    };

    setConnectionStatus('Connecting...');
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    reconnectCheckAttemptsRef.current = 0;
    lastHeartbeatTimeRef.current = Date.now();

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const token = connectionTokenRef.current || getAuthToken();
    if (token && isAuthenticated()) {
      connectWebSocket(token, true); // true = reset_session
    }

    scheduleScrollToBottom('smooth');
  };

  // Function to handle suggestion card click
  const handleSuggestionClick = (text) => {
    handleSendMessage(text);
  };

  // ChatGPT-like auto-scroll function
  const scrollToBottom = (behavior = 'smooth', force = false) => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
    
    // Always scroll if forced (user sent message) or if user is at bottom
    if (force || isAtBottom) {
      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: behavior
        });
      });
      
      // Additional scroll after a short delay to handle dynamic content
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 50);
      
      // Force scroll again after content is fully rendered
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 150);
      
      setShowScrollButton(false);
      setHasNewMessages(false);
    } else {
      // User has scrolled up, show new message indicator
      setHasNewMessages(true);
    }
  };

  // Handle scroll events to show/hide scroll button
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
    
    setShowScrollButton(!isAtBottom);
    
    // Clear new messages indicator when user scrolls to bottom
    if (isAtBottom && hasNewMessages) {
      setHasNewMessages(false);
    }

    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 80;
    const shouldLoadMore = nearBottom;

    if (
      shouldLoadMore &&
      sessionMessagesHasMoreRef.current &&
      !sessionMessagesLoadingRef.current &&
      activeLoadedSessionIdRef.current
    ) {
      loadSessionMessagesPage(
        activeLoadedSessionIdRef.current,
        sessionMessagesOffsetRef.current,
        true
      );
    }
  };

  // Scroll to show new message when it appears (for both user and assistant messages)
  useEffect(() => {
    if (suppressAutoScrollRef.current) return;

    const currentMessageCount = messages.length;
    const lastMessageIndex = currentMessageCount - 1;
    
    // Check if a new message was added
    if (currentMessageCount > 0 && lastMessageIndex !== lastMessageIndexRef.current) {
      lastMessageIndexRef.current = lastMessageIndex;
      
      // Immediate scroll to bottom
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
      
      // Force scroll to bottom when new message appears
      scrollToBottom('smooth', true);
      
      // Additional scrolls to ensure it works
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        scrollToBottom('smooth', true);
      }, 50);
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        scrollToBottom('smooth', true);
      }, 150);
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        scrollToBottom('smooth', true);
      }, 300);
    }
    
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [messages]);

  useEffect(() => {
    if (showProjectStartView) return;
    if (!messages || messages.length === 0) return;
    if (forceScrollAfterHistoryLoadRef.current) {
      forceScrollToBottomWithRetries(0);
      setTimeout(() => {
        forceScrollAfterHistoryLoadRef.current = false;
      }, 1200);
      return;
    }
    scheduleScrollToBottom('auto');
  }, [showProjectStartView, messages.length]);

  // Scroll to bottom when assistant starts streaming response
  useEffect(() => {
    if (isLoading && currentResponse && currentResponse.length > 0 && !hasScrolledForResponseRef.current) {
      hasScrolledForResponseRef.current = true;
      
      // Immediate scroll when streaming starts
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
      
      // Force scroll when streaming starts
      scrollToBottom('smooth', true);
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        scrollToBottom('smooth', true);
      }, 100);
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
        scrollToBottom('smooth', true);
      }, 300);
    }
    
    // Reset the flag when response ends
    if (!isLoading && !currentResponse) {
      hasScrolledForResponseRef.current = false;
    }
  }, [isLoading, currentResponse]);

  // Additional scroll trigger when messages array changes (only for user messages)
  useEffect(() => {
    if (messages.length > 0) {
      // Check if the last message is from user
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.role === 'user') {
        // Immediate scroll for user messages
        scrollToBottom('smooth', true);
        // Additional scroll after content is rendered
        setTimeout(() => {
          scrollToBottom('smooth', true);
        }, 100);
      }
      // Don't auto-scroll for assistant messages - let user scroll manually
    }
  }, [messages.length, messages]);

  // Keyboard shortcut for scrolling to bottom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'End') {
        e.preventDefault();
        scrollToBottom('smooth', true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);



  // Handle textarea auto-resize and scroll adjustment
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 88) + 'px';
      
      // Always scroll to bottom when typing to keep input visible
      if (message.length > 0) {
        // Immediate scroll for better responsiveness
        scrollToBottom('auto', true);
        
        // Additional scroll after height adjustment
        setTimeout(() => {
          scrollToBottom('smooth', true);
        }, 50);
      }
    }
  }, [message]);

  // Set to true to show predefined message cards (temporarily hidden)
  const SHOW_PREDEFINED_MESSAGES = false;
  const suggestionCards = [
    { title: "Create a Policy or Procedure (Tailored)", icon: null },
    { title: "Create a Risk Assessment or SWMS", icon: null },
    { title: "Run a Deep Compliance Audit", icon: null },
    { title: "Audit a Photo or Site Condition", icon: null }
  ];

  const showWelcome = messages.length === 0;

  // Delete chat image API call
  const deleteChatImage = async (url) => {
    const token = getAuthToken();
    try {
      // Ensure backend receives full image URL
      let fullUrlToSend = url || '';
      if (fullUrlToSend && !/^https?:\/\//i.test(fullUrlToSend)) {
        fullUrlToSend = PROFILE_COMPANY_PIC + fullUrlToSend.replace(/^\//, '');
      }
      const response = await fetch(getApiUrl(COMPANY_API.DELETE_CHAT_IMAGE), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({ url: fullUrlToSend })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedImage(null);
        setImagePreviewUrl(null);
        setUploadedImageUrl(null);
      } else {
        setAlertConfig({
          type: 'error',
          title: 'Image Deletion Failed',
          message: 'Failed to delete image. Please try again.'
        });
        setShowAlert(true);
      }
    } catch (err) {
      setAlertConfig({
        type: 'error',
        title: 'Image Deletion Failed',
        message: 'Failed to delete image. Please try again.'
      });
      setShowAlert(true);
    }
  };

  // Delete chat document API call
  const deleteChatDocument = async (url) => {
    const token = getAuthToken();
    try {
      // Ensure backend receives full file URL
      let fullUrlToSend = url || '';
      if (fullUrlToSend && !/^https?:\/\//i.test(fullUrlToSend)) {
        fullUrlToSend = PROFILE_COMPANY_PIC + fullUrlToSend.replace(/^\//, '');
      }
      const response = await fetch(getApiUrl(COMPANY_API.DELETE_CHAT_IMAGE), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({ url: fullUrlToSend })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedDocument(null);
        setUploadedDocumentUrl(null);
        setDocumentContext(null);
      } else {
        setAlertConfig({
          type: 'error',
          title: 'Document Deletion Failed',
          message: 'Failed to delete document. Please try again.'
        });
        setShowAlert(true);
      }
    } catch (err) {
      setAlertConfig({
        type: 'error',
        title: 'Document Deletion Failed',
        message: 'Failed to delete document. Please try again.'
      });
      setShowAlert(true);
    }
  };

  // Typing indicator animation for assistant thinking
  const TypingIndicator = ({ mode }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      margin: '8px 0 8px 0',
      minHeight: 40,
      flexWrap: 'wrap'
    }}>
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: mode === 'dark'
          ? 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)'
          : 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2
      }}>
        <img src={icsAssistant} alt="Assistant" style={{ width: 32, height: 32, filter: 'none' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!showThinkingText && (
          <div className="typing-indicator-bounce">
            <span></span><span></span><span></span>
          </div>
        )}
        {showThinkingText && (
          <div
            className="shimmer-text"
            style={{
              fontSize: 14,
              lineHeight: '18px',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            Thinking longer for better answer..
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="assistant-page"
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        // Used by Assistant.css to offset the main chat column.
        '--assistant-right-sidebar-width': `${isRightSidebarCollapsed ? RIGHT_SIDEBAR_COLLAPSED_WIDTH : RIGHT_SIDEBAR_WIDTH}px`
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <AssistantRightSidebar
        mode={mode}
        brandIconSrc={icsAssistant}
        collapsed={isRightSidebarCollapsed}
        onCollapsedChange={setIsRightSidebarCollapsed}
        projects={staticProjects}
        recents={chatHistory || []}
        activeProjectId={activeProjectId}
        activeRecentId={activeChatHistoryId}
        onProjectClick={(p) => {
          const projectId = p?.conversation_project_id ?? p?.id ?? p?.project_id ?? null;
          const projectTitle = p?.name || p?.title || p?.project_name || 'Project';
          setSelectedAssistantProject({
            id: projectId != null ? String(projectId) : null,
            title: projectTitle
          });
          setShowProjectStartView(true);
        }}
        onMoreProjects={() => {
          navigate('/projects');
        }}
        onNewChatClick={() => {
          setShowProjectStartView(false);
          resetConversation();
        }}
        onRecentClick={(item) => {
          setShowProjectStartView(false);
          switchConversationToHistory(item);
        }}
      />

      {showProjectStartView && (
        <AssistantProjectStartView
          mode={mode}
          projectId={selectedAssistantProject?.id}
          projectTitle={selectedAssistantProject?.title || 'Project'}
          items={(chatHistory || []).map((h) => ({
            id: h?.id ?? h?.sessionId,
            title: h?.title || 'Conversation',
            preview: Array.isArray(h?.messages)
              ? (h.messages.find((m) => m?.role === 'user' && m?.text)?.text || '')
              : '',
            createdAt: h?.createdAt
          }))}
          onSubmit={(text, attachmentData) => {
            setShowProjectStartView(false);
            startProjectConversation(text, attachmentData);
          }}
          onSessionClick={(item) => {
            setShowProjectStartView(false);
            switchConversationToHistory(item);
          }}
        />
      )}

      <div
        style={{
          height: '100%',
          display: showProjectStartView ? 'none' : 'flex',
          flexDirection: 'column',
          backgroundColor: mode === 'dark' ? '#0f0f0f' : 'transparent',
          color: mode === 'dark' ? '#fff' : '#000',
          minHeight: 0,
          position: 'relative',
        }}
      >
        
        {/* Drag and drop overlay */}
        {isDragOver && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: mode === 'dark' ? 'rgba(38, 195, 192, 0.1)' : 'rgba(38, 195, 192, 0.05)',
              border: `2px dashed ${mode === 'dark' ? '#26c3c0' : '#26c3c0'}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              backdropFilter: 'blur(2px)',
            }}
          >
                          <div style={{
                textAlign: 'center',
                color: mode === 'dark' ? '#26c3c0' : '#26c3c0',
                fontSize: '18px',
                fontWeight: '600',
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7,10 12,15 17,10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </div>
                Drop your file here
              </div>
          </div>
        )}

        {/* Copy success feedback */}
        {copySuccess && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: mode === 'dark' ? '#26c3c0' : '#26c3c0',
              color: '#fff',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              zIndex: 2000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              animation: 'copySuccessFade 2s ease-in-out',
            }}
          >
            ✓ Content copied successfully!
          </div>
        )}
        <div 
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0, 
            backgroundColor: 'transparent',
            position: 'relative',
          }}
          className="assistant-main-column"
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(true);
          }}
        >
          {showWelcome ? (
            <div
              className="assistant-welcome-content"
              style={{
                width: '100%',
                maxWidth: 'var(--assistant-content-max-width, 760px)',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '24px',
                paddingTop: 48,
                paddingBottom: 0,
                flex: 1,
                minHeight: 0,
                backgroundColor: 'transparent',
              }}
            >
              {/* Assistant Icon */}
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '10px',
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(24px)'
              }}>
                <img src={icsAssistant} alt="Assistant" style={{ width: 52, height: 52, filter: 'none' }} />
              </div>
              {/* Welcome Text */}
              <div style={{ textAlign: 'center', maxWidth: 913 }}>
                <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#26c3c0', margin: '0', lineHeight: '1.15' }}>
                  {userFirstName ? `Hi ${userFirstName}! What can I help with ?` : 'Hi! What can I help with ?'}
                </h1>
              </div>
              {/* Suggestion Cards (temporarily hidden - set SHOW_PREDEFINED_MESSAGES to true to show) */}
              {SHOW_PREDEFINED_MESSAGES && (
              <div
                className="assistant-welcome-suggestions"
                style={{
                  display: 'flex',
                  gap: 20,
                  justifyContent: 'center',
                  width: '100%',
                  marginTop: 24,
                  marginBottom: 8,
                }}
              >
                {suggestionCards.map((card, index) => (
                  <div
                    key={index}
                    style={{
                      flex: '1 1 0',
                      minWidth: 220,
                      maxWidth: 260,
                      height: 60,
                      borderRadius: 14,
                      background: mode === 'dark' ? '#232323' : '#f4f4f4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 20px',
                      cursor: 'pointer',
                      boxShadow: mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.10)' : '0 1px 4px rgba(38,195,192,0.04)',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => handleSuggestionClick(card.title)}
                  >
                    <span style={{
                      fontSize: 16,
                      color: mode === 'dark' ? '#b0b0b0' : '#232323',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      whiteSpace: 'pre-line',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      flex: 1,
                    }}>
                      {card.title}
                    </span>
                  </div>
                ))}
              </div>
              )}
              
              {/* Flexible spacer to push input to bottom if needed */}
              <div style={{ flex: 1, minHeight: 0 }} />
            </div>
          ) : (
            // Chat area
            <div
              className="assistant-chat-area"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: 'var(--assistant-content-max-width, 760px)',
                margin: '0 auto',
                minHeight: 0,
                paddingTop: 48,
                paddingBottom: 80,
                position: 'relative',
                backgroundColor: 'transparent',
              }}
            >
              {/* New Chat Button - Fixed at top of conversation */}
              {messages.length > 0 && (
                <div
                  className="assistant-new-chat-wrap"
                  style={{
                    position: 'fixed',
                    top: 80,
                    left: 235,
                    right: isRightSidebarCollapsed ? RIGHT_SIDEBAR_COLLAPSED_WIDTH : RIGHT_SIDEBAR_WIDTH,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 99,
                    pointerEvents: 'none',
                    padding: '12px 0',
                  }}
                >
                  <div style={{ pointerEvents: 'auto' }}>
                    <button
                      onClick={resetConversation}
                      disabled={isLoading}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 20px',
                        borderRadius: '12px',
                        border: `1.5px solid ${mode === 'dark' ? '#26c3c0' : '#26c3c0'}`,
                        background: mode === 'dark' 
                          ? 'linear-gradient(135deg, rgba(38, 195, 192, 0.35) 0%, rgba(38, 195, 192, 0.25) 100%)'
                          : 'linear-gradient(135deg, rgba(38, 195, 192, 0.25) 0%, rgba(38, 195, 192, 0.18) 100%)',
                        color: '#26c3c0',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        opacity: isLoading ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        boxShadow: mode === 'dark' 
                          ? '0 2px 8px rgba(38, 195, 192, 0.3)' 
                          : '0 2px 8px rgba(38, 195, 192, 0.25)',
                        backdropFilter: 'blur(10px)',
                      }}
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.target.style.background = mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(38, 195, 192, 0.45) 0%, rgba(38, 195, 192, 0.35) 100%)'
                            : 'linear-gradient(135deg, rgba(38, 195, 192, 0.35) 0%, rgba(38, 195, 192, 0.28) 100%)';
                          e.target.style.transform = 'scale(1.05)';
                          e.target.style.boxShadow = mode === 'dark' 
                            ? '0 4px 12px rgba(38, 195, 192, 0.4)' 
                            : '0 4px 12px rgba(38, 195, 192, 0.35)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.target.style.background = mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(38, 195, 192, 0.35) 0%, rgba(38, 195, 192, 0.25) 100%)'
                            : 'linear-gradient(135deg, rgba(38, 195, 192, 0.25) 0%, rgba(38, 195, 192, 0.18) 100%)';
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = mode === 'dark' 
                            ? '0 2px 8px rgba(38, 195, 192, 0.3)' 
                            : '0 2px 8px rgba(38, 195, 192, 0.25)';
                        }
                      }}
                      title="Start a new conversation"
                    >
                      <RestartAltIcon style={{ fontSize: 18 }} />
                      <span>New Chat</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Extra padding at top to account for fixed button */}
              {messages.length > 0 && (
                <div style={{ height: 70 }} />
              )}
              
              <div
                ref={chatContainerRef}
                onScroll={handleScroll}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '0 0 16px 0',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {messages.map((msg, idx) => {
                  const isLastMessage = idx === messages.length - 1;
                  return msg.role === 'assistant' ? (
                    <div key={idx} ref={isLastMessage ? lastMessageRef : null} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      {/* Assistant Icon (same as sidebar) */}
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)'
                          : 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: 2
                      }}>
                        <img src={icsAssistant} alt="Assistant" style={{ width: 32, height: 32, filter: 'none' }} />
                      </div>
                      {/* Assistant Bubble with markdown rendering */}
                      <div
                        style={{
                          background: mode === 'dark' ? '#232323' : '#e0f7f7',
                          color: mode === 'dark' ? '#fff' : '#000',
                          borderRadius: '16px 16px 16px 4px',
                          padding: '12px 18px',
                          width: '100%',
                          maxWidth: '100%',
                          fontSize: 16,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          marginBottom: 2,
                          alignSelf: 'flex-start',
                          wordBreak: 'break-word',
                          position: 'relative',
                          overflow: 'visible',
                          minWidth: 0
                        }}
                      >
                        {/* Copy button */}
                        <button
                          onClick={() => copyToClipboard(msg.text, true)}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.6,
                            transition: 'opacity 0.2s ease',
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '1'}
                          onMouseLeave={(e) => e.target.style.opacity = '0.6'}
                          title="Copy message"
                        >
                          <ContentCopyIcon 
                            style={{ 
                              fontSize: 16, 
                              color: mode === 'dark' ? '#fff' : '#000' 
                            }} 
                          />
                        </button>
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Override default paragraph styling to inherit color
                            p: ({node, ...props}) => (
                              <p 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            // Override default heading styling to inherit color
                            h1: ({node, ...props}) => (
                              <h1 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            h2: ({node, ...props}) => (
                              <h2 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            h3: ({node, ...props}) => (
                              <h3 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            h4: ({node, ...props}) => (
                              <h4 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            h5: ({node, ...props}) => (
                              <h5 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            h6: ({node, ...props}) => (
                              <h6 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            // Override list styling to inherit color
                            ul: ({node, ...props}) => (
                              <ul 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            ol: ({node, ...props}) => (
                              <ol 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            li: ({node, ...props}) => (
                              <li 
                                {...props} 
                                style={{
                                  color: 'inherit'
                                }}
                              />
                            ),
                            // Override code styling to inherit color
                            code: ({node, ...props}) => (
                              <code 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  fontSize: '0.9em'
                                }}
                              />
                            ),
                            pre: ({node, ...props}) => (
                              <pre 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                  padding: '12px',
                                  borderRadius: '8px',
                                  overflow: 'auto',
                                  margin: '0.5em 0'
                                }}
                              />
                            ),
                            // Override link styling to inherit color and open in new tab
                            a: ({node, ...props}) => (
                              <a 
                                {...props} 
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: mode === 'dark' ? '#26c3c0' : '#26c3c0',
                                  textDecoration: 'underline',
                                  cursor: 'pointer'
                                }}
                              />
                            ),
                            // Override blockquote styling to inherit color
                            blockquote: ({node, ...props}) => (
                              <blockquote 
                                {...props} 
                                style={{
                                  color: 'inherit',
                                  borderLeft: `4px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                                  paddingLeft: '12px',
                                  margin: '0.5em 0',
                                  fontStyle: 'italic'
                                }}
                              />
                            ),
                            table: ({node, ...props}) => (
                              <div 
                                className="table-scroll-wrapper"
                                style={{
                                  overflowX: 'auto',
                                  overflowY: 'visible',
                                  margin: '8px 0',
                                  maxWidth: '100%',
                                  WebkitOverflowScrolling: 'touch'
                                }}
                              >
                                <table 
                                  {...props} 
                                  style={{
                                    borderCollapse: 'collapse',
                                    // Let the table grow wider than the bubble when needed
                                    width: 'max-content',
                                    // But still fill the bubble for small tables
                                    minWidth: '100%',
                                    border: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    display: 'table',
                                    tableLayout: 'auto'
                                  }}
                                />
                              </div>
                            ),
                            thead: ({node, ...props}) => (
                              <thead 
                                {...props} 
                                style={{
                                  backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                  borderBottom: mode === 'dark' ? '1px solid #444' : '1px solid #000'
                                }}
                              />
                            ),
                            tbody: ({node, ...props}) => (
                              <tbody {...props} />
                            ),
                            tr: ({node, ...props}) => (
                              <tr 
                                {...props} 
                                style={{
                                  borderBottom: mode === 'dark' ? '1px solid #444' : '1px solid #000'
                                }}
                              />
                            ),
                            th: ({node, children, ...props}) => (
                              <th 
                                {...props} 
                                style={{
                                  padding: '12px 16px',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: mode === 'dark' ? '#fff' : '#000',
                                  borderRight: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                  // Wider minimum so each column has decent width
                                  minWidth: '160px',
                                  maxWidth: '260px',
                                  // Allow header text to wrap into multiple lines
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {renderMarkdownCellChildren(children)}
                              </th>
                            ),
                            td: ({node, children, ...props}) => (
                              <td 
                                {...props} 
                                style={{
                                  padding: '12px 16px',
                                  borderRight: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                  color: mode === 'dark' ? '#fff' : '#000',
                                  // Match column sizing rules with header
                                  minWidth: '160px',
                                  maxWidth: '260px',
                                  // Wrap long content into multiple lines instead of a single long line
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {renderMarkdownCellChildren(children)}
                              </td>
                            )
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ) : msg.role === 'user' ? (
                                         <div
                       key={idx}
                       ref={isLastMessage ? lastMessageRef : null}
                       className="assistant-message user-bubble"
                       style={{
                         alignSelf: 'flex-end',
                         background: '#26c3c0',
                         color: '#fff',
                         borderRadius: '16px 16px 4px 16px',
                         padding: '12px 18px',
                         width: '60%',
                         fontSize: 16,
                         boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                         marginBottom: 2,
                         wordBreak: 'break-word',
                         display: 'flex',
                         flexDirection: 'column',
                         gap: 8,
                         alignItems: 'flex-start'
                       }}
                     >
                       {msg.image && (
                         <img src={msg.image} alt="user upload" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, marginBottom: 6, alignSelf: 'flex-start' }} />
                       )}
                       {msg.document && (
                         <div style={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: 8,
                           padding: '8px 12px',
                           background: 'rgba(255,255,255,0.1)',
                           borderRadius: 8,
                           marginBottom: 6,
                           alignSelf: 'flex-start',
                           maxWidth: 200
                         }}>
                           <InsertDriveFileOutlinedIcon 
                             style={{ 
                               fontSize: 20, 
                               color: '#fff',
                               flexShrink: 0
                             }} 
                           />
                           <div style={{ 
                             flex: 1, 
                             minWidth: 0,
                             display: 'flex',
                             flexDirection: 'column'
                           }}>
                             <div style={{
                               fontSize: 12,
                               fontWeight: '500',
                               color: '#fff',
                               whiteSpace: 'nowrap',
                               overflow: 'hidden',
                               textOverflow: 'ellipsis'
                             }}>
                               {msg.document.name}
                             </div>
                             <div style={{
                               fontSize: 10,
                               color: 'rgba(255,255,255,0.7)',
                               marginTop: 2
                             }}>
                               {(msg.document.size / 1024 / 1024).toFixed(2)} MB
                             </div>
                           </div>
                         </div>
                       )}
                      {!!String(msg.text || '').trim() && (
                        <div style={{ alignSelf: 'flex-start', textAlign: 'left' }}>
                          {msg.text}
                        </div>
                      )}
                     </div>
                  ) : (
                    <div key={idx} style={{ color: '#ff4444', fontStyle: 'italic', margin: '8px 0' }}>{msg.text}</div>
                  )
                })}
                {/* Streaming assistant response */}
                {isLoading && currentResponse && (
                  <div ref={streamingBubbleRef} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(161, 161, 161, 0.35) 0%, rgba(161, 161, 161, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 2
                    }}>
                      <img src={icsAssistant} alt="Assistant" style={{ width: 32, height: 32, filter: 'none' }} />
                    </div>
                    <div
                      className="assistant-message streaming-bubble"
                      style={{
                        background: mode === 'dark' ? '#232323' : '#e0f7f7',
                        color: mode === 'dark' ? '#fff' : '#000',
                        borderRadius: '16px 16px 16px 4px',
                        padding: '12px 18px',
                        width: '100%',
                        maxWidth: '100%',
                        fontSize: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        marginBottom: 2,
                        alignSelf: 'flex-start',
                        wordBreak: 'break-word',
                        fontStyle: 'italic',
                        opacity: 0.8,
                        position: 'relative',
                        overflow: 'visible',
                        minWidth: 0
                      }}
                    >
                      {/* Copy button for streaming response */}
                      <button
                        onClick={() => copyToClipboard(currentResponse, true)}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.6,
                          transition: 'opacity 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                        onMouseLeave={(e) => e.target.style.opacity = '0.6'}
                        title="Copy message"
                      >
                        <ContentCopyIcon 
                          style={{ 
                            fontSize: 16, 
                            color: mode === 'dark' ? '#fff' : '#000' 
                          }} 
                        />
                      </button>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Override default paragraph styling to inherit color
                          p: ({node, ...props}) => (
                            <p 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          // Override default heading styling to inherit color
                          h1: ({node, ...props}) => (
                            <h1 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          h2: ({node, ...props}) => (
                            <h2 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          h3: ({node, ...props}) => (
                            <h3 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          h4: ({node, ...props}) => (
                            <h4 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          h5: ({node, ...props}) => (
                            <h5 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          h6: ({node, ...props}) => (
                            <h6 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          // Override list styling to inherit color
                          ul: ({node, ...props}) => (
                            <ul 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          ol: ({node, ...props}) => (
                            <ol 
                              {...props} 
                              style={{
                                color: 'inherit',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          li: ({node, ...props}) => (
                            <li 
                              {...props} 
                              style={{
                                color: 'inherit'
                              }}
                            />
                          ),
                          // Override code styling to inherit color
                          code: ({node, ...props}) => (
                            <code 
                              {...props} 
                              style={{
                                color: 'inherit',
                                backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                fontSize: '0.9em'
                              }}
                            />
                          ),
                          pre: ({node, ...props}) => (
                            <pre 
                              {...props} 
                              style={{
                                color: 'inherit',
                                backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                padding: '12px',
                                borderRadius: '8px',
                                overflow: 'auto',
                                margin: '0.5em 0'
                              }}
                            />
                          ),
                          // Override link styling to inherit color and open in new tab
                          a: ({node, ...props}) => (
                            <a 
                              {...props} 
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: mode === 'dark' ? '#26c3c0' : '#26c3c0',
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                            />
                          ),
                          // Override blockquote styling to inherit color
                          blockquote: ({node, ...props}) => (
                            <blockquote 
                              {...props} 
                              style={{
                                color: 'inherit',
                                borderLeft: `4px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                                paddingLeft: '12px',
                                margin: '0.5em 0',
                                fontStyle: 'italic'
                              }}
                            />
                          ),
                          table: ({node, ...props}) => (
                            <div 
                              className="table-scroll-wrapper"
                              style={{
                                overflowX: 'auto',
                                overflowY: 'visible',
                                margin: '8px 0',
                                maxWidth: '100%',
                                WebkitOverflowScrolling: 'touch'
                              }}
                            >
                              <table 
                                {...props} 
                                style={{
                                  borderCollapse: 'collapse',
                                  // Let the table grow wider than the bubble when needed
                                  width: 'max-content',
                                  // But still fill the bubble for small tables
                                  minWidth: '100%',
                                  border: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  display: 'table',
                                  tableLayout: 'auto'
                                }}
                              />
                            </div>
                          ),
                          thead: ({node, ...props}) => (
                            <thead 
                              {...props} 
                              style={{
                                backgroundColor: mode === 'dark' ? '#333' : '#f0f0f0',
                                borderBottom: mode === 'dark' ? '1px solid #444' : '1px solid #000'
                              }}
                            />
                          ),
                          tbody: ({node, ...props}) => (
                            <tbody {...props} />
                          ),
                          tr: ({node, ...props}) => (
                            <tr 
                              {...props} 
                              style={{
                                borderBottom: mode === 'dark' ? '1px solid #444' : '1px solid #000'
                              }}
                            />
                          ),
                          th: ({node, children, ...props}) => (
                            <th 
                              {...props} 
                              style={{
                                padding: '12px 16px',
                                textAlign: 'left',
                                fontWeight: '600',
                                color: mode === 'dark' ? '#fff' : '#000',
                                borderRight: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                // Wider minimum so each column has decent width
                                minWidth: '160px',
                                maxWidth: '260px',
                                // Allow header text to wrap into multiple lines
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                              }}
                            >
                              {renderMarkdownCellChildren(children)}
                            </th>
                          ),
                          td: ({node, children, ...props}) => (
                            <td 
                              {...props} 
                              style={{
                                padding: '12px 16px',
                                borderRight: mode === 'dark' ? '1px solid #444' : '1px solid #000',
                                color: mode === 'dark' ? '#fff' : '#000',
                                // Match column sizing rules with header
                                minWidth: '160px',
                                maxWidth: '260px',
                                // Wrap long content into multiple lines instead of a single long line
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                              }}
                            >
                              {renderMarkdownCellChildren(children)}
                            </td>
                          )
                        }}
                      >
                        {currentResponse}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
                {/* Typing indicator when assistant is thinking */}
                {isLoading && !currentResponse && (
                  <TypingIndicator mode={mode} />
                )}
                <div ref={chatEndRef} />
                
                {/* Scroll to bottom button */}
                {showScrollButton && (
                  <button
                    className="assistant-scroll-to-bottom"
                    onClick={() => scrollToBottom('smooth', true)}
                    style={{
                      position: 'absolute',
                      bottom: 20,
                      right: 20,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: hasNewMessages ? '#26c3c0' : (mode === 'dark' ? '#232323' : '#fff'),
                      border: `1px solid ${hasNewMessages ? '#26c3c0' : (mode === 'dark' ? '#444' : '#ddd')}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 10,
                      transition: 'all 0.2s ease',
                    }}
                    title={hasNewMessages ? "New messages - Scroll to bottom" : "Scroll to bottom"}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={hasNewMessages ? '#fff' : (mode === 'dark' ? '#fff' : '#333')} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                    {hasNewMessages && (
                      <div style={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#ff4444',
                        border: '2px solid #fff',
                      }} />
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Chat Input Section (always at bottom) */}
        <div
          className="assistant-input-bar"
          style={{
            position: 'fixed',
            left: 235,
            right: isRightSidebarCollapsed ? RIGHT_SIDEBAR_COLLAPSED_WIDTH : RIGHT_SIDEBAR_WIDTH,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            padding: '0 0 8px 0',
            background: mode === 'dark' ? '#111' : '#fff',
            zIndex: 100,
          }}
        >
          {/* Chat Input Row */}
          <div
            style={{
              width: '100%',
              maxWidth: 'var(--assistant-content-max-width, 760px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #18191B 0%, #232323 100%)'
                  : 'linear-gradient(135deg, #f8f9fa 0%, #e0f7f7 100%)',
                border: `1px solid ${mode === 'dark' ? '#232323' : '#ddd'}`,
                borderRadius: 14,
                padding: '0 0 0 0',
                minHeight: 56,
                boxShadow: mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.12)' : '0 1px 4px rgba(38,195,192,0.04)',
                position: 'relative',
              }}
            >
              {/* Attach Icon inside input */}
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 12px',
                  cursor: 'pointer',
                  height: '100%',
                }}
                tabIndex={-1}
                aria-label="Attach file"
                onClick={handleAttachClick}
              >
                <AttachFileIcon style={{ color: mode === 'dark' ? '#b0b0b0' : '#b0b0b0', fontSize: 22 }} />
              </button>
              {/* Attach File Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <MenuItem onClick={handleAddImage}>
                  <ImageOutlinedIcon style={{ marginRight: 8, color: mode === 'dark' ? '#26c3c0' : '#009391' }} />
                  Image
                </MenuItem>
                <MenuItem onClick={handleAddFile}>
                  <InsertDriveFileOutlinedIcon style={{ marginRight: 8, color: mode === 'dark' ? '#26c3c0' : '#009391' }} />
                  File
                </MenuItem>
              </Menu>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                // accept is set dynamically
              />
              {/* Image preview above input (smaller, only delete icon) */}
              {imagePreviewUrl && (
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  margin: '12px 12px 12px 12px', // increased bottom margin for spacing
                  borderRadius: 14,
                  boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.24)' : '0 2px 8px rgba(38,195,192,0.08)',
                  background: mode === 'dark' ? '#18191B' : '#fff',
                  padding: 0,
                  width: 220,
                  minHeight: 80
                }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreviewUrl}
                      alt="preview"
                      style={{
                        width: '100%',
                        maxHeight: 90,
                        objectFit: 'cover',
                        borderRadius: 14,
                        display: 'block',
                        background: '#f4f4f4'
                      }}
                    />
                    {/* Only delete icon button */}
                    <button
                      onClick={() => { if (uploadedImageUrl) { deleteChatImage(uploadedImageUrl); } else { setSelectedImage(null); setImagePreviewUrl(null); setUploadedImageUrl(null); } }}
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: '#18191Bcc',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                        fontSize: 16
                      }}
                      title="Remove image"
                      tabIndex={-1}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              {/* Document preview above input */}
              {selectedDocument && (
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  margin: '12px 12px 12px 12px',
                  borderRadius: 14,
                  boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.24)' : '0 2px 8px rgba(38,195,192,0.08)',
                  background: mode === 'dark' ? '#18191B' : '#fff',
                  padding: '12px',
                  width: 220,
                  minHeight: 60
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    width: '100%'
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: mode === 'dark' ? '#333' : '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <InsertDriveFileOutlinedIcon 
                        style={{ 
                          fontSize: 18, 
                          color: mode === 'dark' ? '#26c3c0' : '#26c3c0' 
                        }} 
                      />
                    </div>
                    <div style={{ 
                      flex: 1, 
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        fontSize: 12,
                        fontWeight: '500',
                        color: mode === 'dark' ? '#fff' : '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {selectedDocument.name}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: mode === 'dark' ? '#888' : '#666',
                        marginTop: 2
                      }}>
                        {(selectedDocument.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      onClick={() => { 
                        if (uploadedDocumentUrl) { 
                          deleteChatDocument(uploadedDocumentUrl); 
                        } else { 
                          setSelectedDocument(null); 
                          setUploadedDocumentUrl(null); 
                        } 
                      }}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#18191Bcc',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                        fontSize: 12,
                        flexShrink: 0
                      }}
                      title="Remove document"
                      tabIndex={-1}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              <textarea
                ref={textareaRef}
                placeholder={
                  (imagePreviewUrl || selectedDocument) 
                    ? "Add a message about the file or image..." 
                    : "Ask anything"
                }
                value={message}
                onChange={e => {
                  setMessage(e.target.value);
                  // Scroll to bottom immediately when typing
                  setTimeout(() => scrollToBottom('auto', true), 0);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const hasText = message.trim().length > 0;
                    const hasAttachment = !!imagePreviewUrl || !!selectedDocument || (documentContext && documentContext.trim().length > 0);
                    // Allow sending when there is either text or an attachment/context, and not loading
                    if ((hasText || hasAttachment) && !isLoading) {
                      handleSendMessage();
                    }
                  }
                }}
                onFocus={() => {
                  // Scroll to bottom when textarea is focused
                  setTimeout(() => scrollToBottom('smooth', true), 100);
                }}
                rows={1}
                style={{
                  flex: 1,
                  resize: 'none',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: '16px',
                  color: mode === 'dark' ? '#fff' : '#333',
                  fontFamily: 'inherit',
                  padding: '18px 20px 18px 0',
                  minWidth: 0,
                  paddingLeft: 0,
                  lineHeight: '22px', // 1 row = 22px, 4 rows = 88px
                  maxHeight: 88,
                  overflowY: 'auto',
                  scrollbarWidth: 'none', // Firefox
                }}
                disabled={isLoading}
                spellCheck={true}
              />
              {/* Hide scrollbar for textarea and style table scrollbars */}
              <style>{`
                textarea::-webkit-scrollbar { display: none; }
                /* Table horizontal scrollbar styling */
                .table-scroll-wrapper::-webkit-scrollbar {
                  height: 8px;
                }
                .table-scroll-wrapper::-webkit-scrollbar-track {
                  background: ${mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
                  border-radius: 4px;
                }
                .table-scroll-wrapper::-webkit-scrollbar-thumb {
                  background: ${mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
                  border-radius: 4px;
                }
                .table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
                  background: ${mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
                }
                /* Firefox scrollbar styling */
                .table-scroll-wrapper {
                  scrollbar-width: thin;
                  scrollbar-color: ${mode === 'dark' ? 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)'};
                }
              `}</style>
              <button
                onClick={handleSendMessage}
                disabled={(!message.trim() && !imagePreviewUrl && !selectedDocument && !(documentContext && documentContext.trim().length > 0)) || isLoading}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#26c3c0',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: ((!message.trim() && !imagePreviewUrl && !selectedDocument && !(documentContext && documentContext.trim().length > 0)) || isLoading) ? 'not-allowed' : 'pointer',
                  opacity: ((!message.trim() && !imagePreviewUrl && !selectedDocument && !(documentContext && documentContext.trim().length > 0)) || isLoading) ? 0.5 : 1,
                  marginRight: 12,
                  marginLeft: 8,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <SendIcon style={{ color: '#fff', fontSize: 24 }} />
              </button>
            </div>
            
            {/* Voice message icon and label hidden for now */}
            
            {/* Disclaimer note */}
            <div style={{
              textAlign: 'center',
              padding: '4px 12px 0 12px',
              fontSize: '13px',
              color: mode === 'dark' ? '#888' : '#666',
              fontStyle: 'italic',
              lineHeight: '1.4'
            }}>
              Complimate AI can make mistakes. Check important info
            </div>
          </div>
        </div>
      </div>
      
      {/* Alert Dialog */}
      <AlertDialog
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText="OK"
      />
    </div>
  );
}

export default Assistant; 
